import { type Ref, useId } from "react";

import {
  Accordion,
  ActionIcon,
  Group,
  Input,
  NumberInput,
  Stack,
  TagsInput,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { UseFormReturnType } from "@mantine/form";

import { IconCircleMinus } from "@tabler/icons-react";

import { TextWrapper } from "@/components/TextWrapper";
import { DotPhraseRichTextField, PHRASE_TEXT_EXTENSIONS } from "@/dot-phrases";
import { toProcedureNoteHtml } from "@/encounters/utils/procedure-notes";
import { ProceduresPickerFormType } from "@/encounters/zod-schemas/procedures";
import { ServiceDateKey } from "@/shared/procedure-code-effective-dates";
import ProcedureCodeDateWarning from "@/shared/procedure-code-effective-dates/components/ProcedureCodeDateWarning";
import { normalizeEmptyHtml } from "@/utils/rich-text";

import ProceduresCodeBadge from "./ProceduresCodeBadge";

/**
 * One selected procedure in the picker's right panel: an accordion with its notes, units,
 * modifiers and billing note, a remove button when removable, and a warning under it when the
 * code is not billable on the service date.
 */
function SelectedProcedureRow({
  code,
  description,
  index,
  form,
  onRemove,
  isRemovable,
  removeButtonRef,
  serviceDate,
}: {
  code: string;
  description: string;
  index: number;
  form: UseFormReturnType<ProceduresPickerFormType>;
  onRemove: () => void;
  isRemovable: boolean;
  /** Lets the panel move focus back to this row after Undo restores it. */
  removeButtonRef?: Ref<HTMLButtonElement>;
  /** The encounter's service date (`yyyy-MM-dd`); a code not billable on it shows a warning. */
  serviceDate?: ServiceDateKey;
}) {
  const noteLabelId = useId();

  return (
    <Stack gap={4}>
      <Accordion
        radius="sm"
        variant="separated"
        chevronPosition="left"
        styles={{
          item: { border: "1px solid var(--mantine-color-cream-4)" },
          content: { paddingTop: 0 },
        }}
      >
        <Accordion.Item bg="white" value={code}>
          {/* The remove icon sits outside the control, at the far end from the chevron, so each has its own hit area. */}
          <Group pr="sm" gap="xs" wrap="nowrap">
            <Tooltip
              withArrow
              openDelay={300}
              position="top-start"
              label="Notes & modifiers"
              events={{ hover: true, focus: true, touch: false }}
            >
              <Accordion.Control flex={1} data-testid={`procedures-picker-procedure-${code}`}>
                <Group gap="sm" wrap="nowrap">
                  <ProceduresCodeBadge code={code} />
                  <TextWrapper flex={1}>{description}</TextWrapper>
                </Group>
              </Accordion.Control>
            </Tooltip>
            {isRemovable && (
              <ActionIcon
                size="sm"
                color="red"
                onClick={onRemove}
                variant="transparent"
                ref={removeButtonRef}
                aria-label="Remove procedure"
                data-testid={`remove-procedure-button-${code}`}
              >
                <IconCircleMinus size={20} />
              </ActionIcon>
            )}
          </Group>
          <Accordion.Panel>
            <Stack gap="md">
              <Input.Wrapper label="Procedure Note" labelProps={{ id: noteLabelId }}>
                <DotPhraseRichTextField
                  showToolbar={false}
                  aria-labelledby={noteLabelId}
                  placeholder="Add a procedure note"
                  extensions={PHRASE_TEXT_EXTENSIONS}
                  data-testid={`procedure-note-input-${code}`}
                  value={toProcedureNoteHtml(form.values.procedures[index].providerNotes)}
                  onChange={(value) =>
                    form.setFieldValue(
                      `procedures.${index}.providerNotes`,
                      normalizeEmptyHtml(value),
                    )
                  }
                />
              </Input.Wrapper>
              <Group grow>
                <NumberInput
                  min={0}
                  label="Unit"
                  allowNegative={false}
                  data-testid={`unit-input-${code}`}
                  {...form.getInputProps(`procedures.${index}.units`)}
                />
                <TagsInput
                  label="Modifier"
                  placeholder="Add a modifier"
                  data-testid={`modifier-input-${code}`}
                  {...form.getInputProps(`procedures.${index}.modifiers`)}
                />
              </Group>
              <TextInput
                label="Billing Note"
                placeholder="Add a billing note"
                data-testid={`billing-note-input-${code}`}
                {...form.getInputProps(`procedures.${index}.billerNotes`)}
              />
            </Stack>
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>
      <ProcedureCodeDateWarning
        code={code}
        serviceDate={serviceDate}
        data-testid={`procedure-warning-${code}`}
      />
    </Stack>
  );
}

export default SelectedProcedureRow;
